import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ChatBubbleOutline as CommentsIcon,
  Close as CloseIcon,
  EmojiEvents as TrophyIcon,
  FitnessCenter as FitnessIcon,
  Launch as LaunchIcon,
  Lightbulb as ReasonIcon,
  Place as PlaceIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { formatDateSafe } from '../../../shared/utils/dateHelpers';
import { SuccessNominationRecord } from '../nomination.types';
import { normalizeClientName } from '../utils/nominationFilters';
import { formatSessionsLabel } from '../utils/trainingHistory';

export interface NominationCommentsDialogProps {
  open: boolean;
  nomination: SuccessNominationRecord | null;
  onClose: () => void;
  onViewClient: (clientId: string) => void;
}

const ROLE_LABEL: Record<SuccessNominationRecord['nominated_by']['role'], string> = {
  USER: 'Coach',
  MANAGER: 'Manager',
  ADMIN: 'Admin',
};

const Section: React.FC<{ icon: React.ReactNode; title: string; body: string; emptyText: string }> = ({
  icon,
  title,
  body,
  emptyText,
}) => (
  <Box>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
      {icon}
      <Typography variant="subtitle2" fontWeight={600}>
        {title}
      </Typography>
    </Stack>
    <Typography
      variant="body2"
      color={body ? 'text.primary' : 'text.secondary'}
      sx={{ whiteSpace: 'pre-wrap', fontStyle: body ? 'normal' : 'italic' }}
    >
      {body || emptyText}
    </Typography>
  </Box>
);

/** Read-only view of a nomination's submitted comments. */
export const NominationCommentsDialog: React.FC<NominationCommentsDialogProps> = ({
  open,
  nomination,
  onClose,
  onViewClient,
}) => {
  const theme = useTheme();
  if (!nomination) return null;

  const clientName = normalizeClientName(nomination.client_name) || nomination.client_name;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              backgroundColor: alpha(theme.palette.warning.main, 0.12),
              color: theme.palette.warning.dark,
            }}
          >
            <TrophyIcon fontSize="small" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" component="span" display="block" noWrap>
              {clientName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Nominated {formatDateSafe(nomination.created_at, 'd MMM yyyy, h:mm a')} ·{' '}
              {formatDistanceToNow(nomination.created_at, { addSuffix: true })}
            </Typography>
          </Box>
          <IconButton aria-label="Close" size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Chip
              size="small"
              variant="outlined"
              label={`By ${nomination.nominated_by_name} · ${ROLE_LABEL[nomination.nominated_by.role]}`}
            />
            <Chip
              size="small"
              variant="outlined"
              color="primary"
              icon={<FitnessIcon />}
              label={`${formatSessionsLabel(nomination.training.sessionsCompleted)} completed`}
            />
            {nomination.client_location && (
              <Chip size="small" variant="outlined" icon={<PlaceIcon />} label={nomination.client_location} />
            )}
            <Chip size="small" variant="outlined" label={`ID ${nomination.client_id}`} />
          </Stack>

          <Section
            icon={<TrophyIcon fontSize="small" color="warning" />}
            title="Specific wins and achievements"
            body={nomination.wins}
            emptyText="No wins recorded."
          />
          <Divider />
          <Section
            icon={<CommentsIcon fontSize="small" color="action" />}
            title="General comments"
            body={nomination.comments}
            emptyText="No general comments were added."
          />
          <Divider />
          <Section
            icon={<ReasonIcon fontSize="small" color="primary" />}
            title="Why this client was nominated"
            body={nomination.reason}
            emptyText="No reason recorded."
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        <Button variant="contained" endIcon={<LaunchIcon />} onClick={() => onViewClient(nomination.client_id)}>
          Open client success
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NominationCommentsDialog;
